import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useNavigate, useParams } from "react-router-dom";

import { getAgentActivities } from "../../api/agents";
import { SearchFilterContainer } from "../../components/SearchFilterTable";
import { OutlineButton } from "../../components/Button";

function safeLower(v) {
  return String(v ?? "").toLowerCase();
}

function formatTs(ts) {
  if (!ts) return "-";
  // backend often stores unix seconds; if you store ms, this still shows a valid date but incorrect range
  const d = new Date(Number(ts) * 1000);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString();
}

export default function AgentActivities() {
  const [setPageTitle] = useOutletContext();
  setPageTitle("Agent Activities");

  const { agentId } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [items, setItems] = useState([]);

  async function load() {
    if (!agentId) return;
    setLoading(true);
    setLoadError("");
    try {
      const res = await getAgentActivities({ agentId, limit, offset });
      setItems(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error(e);
      setLoadError(e?.response?.data?.errorMessage || e?.message || "Failed to load agent activities.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, limit, offset]);

  const filtered = useMemo(() => {
    const q = safeLower(search).trim();
    if (!q) return items;

    return items.filter((x) => {
      const a = safeLower(x?.action);
      const t = safeLower(x?.actionType);
      const m = safeLower(JSON.stringify(x?.metadata || x?.meta || x?.extra || {}));
      return a.includes(q) || t.includes(q) || m.includes(q);
    });
  }, [items, search]);

  const reloadTable = ({ search: s }) => setSearch(s || "");

  const prependWidget = (
    <div className="flex center gap-12">
      <button className="btn outline mini" onClick={() => navigate(-1)}>
        Back
      </button>

      <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
        {[25, 50, 100, 200].map((n) => (
          <option key={n} value={n}>
            {n} / page
          </option>
        ))}
      </select>

      <OutlineButton className="mini" onClick={load} disabled={loading}>
        {loading ? "Loading..." : "Refresh"}
      </OutlineButton>
    </div>
  );

  if (loadError) {
    return <div className="flex center box fs-20">{loadError}</div>;
  }

  return (
    <div className="agents">
      <div className="box mb-32">
        <div className="flex between-center mb-16">
          <h3 className="fs-20">Agent Activities</h3>
          <span className="fs-14 charcoal-80">
            Agent ID: <b>{agentId}</b>
          </span>
        </div>
      </div>

      <SearchFilterContainer
        searchHint="Search activities (type, action, metadata)"
        reloadTable={reloadTable}
        prependWidget={prependWidget}
      >
        <table className="box fs-20 mini">
          <thead>
            <tr>
              <th>Time</th>
              <th>Type</th>
              <th>Action</th>
              <th>Meta</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="center-text">
                  Loading...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className="center-text">
                  No activities found.
                </td>
              </tr>
            ) : (
              filtered.map((x, idx) => (
                <tr key={x?.id || `${idx}`}>
                  <td className="fs-16">{formatTs(x?.createdAt || x?.timestamp || x?.time)}</td>
                  <td className="fs-16">{x?.actionType || "-"}</td>
                  <td className="fs-16">{x?.action || "-"}</td>
                  <td className="fs-14 charcoal-60">
                    {x?.metadata || x?.meta || x?.extra
                      ? JSON.stringify(x?.metadata || x?.meta || x?.extra)
                      : "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </SearchFilterContainer>

      <div className="flex center gap-12 mt-16">
        <button
          className="btn outline mini"
          disabled={offset === 0 || loading}
          onClick={() => setOffset((p) => Math.max(0, p - limit))}
        >
          Prev
        </button>

        <button
          className="btn outline mini"
          disabled={loading || items.length < limit}
          onClick={() => setOffset((p) => p + limit)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
